import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Store} from '@ngrx/store';
import {OrderEntry} from '@spartacus/cart/base/root/models/cart.model';
import {LanguageService} from '@spartacus/core';
import moment from 'moment';
import {combineLatest, Observable, of} from 'rxjs';
import {catchError, map, switchMap} from 'rxjs/operators';
import {TermsAndConditionsActions} from '../cart/store/actions/terms-conditions.actions';
import {
  fromTermsAndConditions,
  TermsAndConditionsCanonicalIdMap
} from '../cart/store/selectors/terms-conditions.selectors';
import {CanonicalId, ConfigService, DcpGlobalMessageService} from '@dcp/configurator';
import {SalesOrg, SalesOrgPayload} from '../model/dcp-config-service/sales-org';
import {DcpNetNewTermsConditions} from '../model/dcp-terms-and-conditions';
import {DcpTemplateTermsConditionsData} from '../model/terms-conditions/dcp-template-terms-conditions-data';
import {DcpProductUtilitiesService} from '../product/services/dcp-product-utilities.service';
import {CountrySelectorService} from './country-selector.service';
import {DcpConfigService} from './dcp-config.service';
import {DcpOrderEntryService} from '@app/services/dcp-order-entry.service';
import {TransactionTypeService} from '@app/services/transactionTypes/transaction-type.service';
import {CONFIGURATION_KEY} from '@app/services/dcp-cart-decorator.service';

/**
 * This service provides methods for consuming terms-conditions-service
 */
@Injectable({
  providedIn: 'root'
})
export class TermsConditionsService {

  constructor(
    private http: HttpClient,
    private configService: ConfigService,
    private productUtilitiesService: DcpProductUtilitiesService,
    private languageService: LanguageService,
    private dcpConfigService: DcpConfigService,
    private countrySelectorService: CountrySelectorService,
    private orderEntryService: DcpOrderEntryService,
    private transactionTypeService: TransactionTypeService,
    private store: Store,
    private dcpGlobalMessageService: DcpGlobalMessageService,
  ) {
  }

  /**
   * Return an observable containing product NET_NEW terms and conditions if it exists
   * for given canonicalId.
   *
   * @param canonicalId
   * @param payload for new T&C endpoint we need to check if its FT is enabled and if yes
   * - add current order entry salesOrg to the request
   *
   */
  public fetchTermsConditionsByCanonicalId(canonicalId: CanonicalId, payload: DcpTemplateTermsConditionsData): Observable<DcpNetNewTermsConditions[] | undefined> {
    if (canonicalId?.entitlementRuleId && canonicalId?.productId?.id) {
      return this.languageService.getActive().pipe(
        switchMap(currentLanguage =>
          this.http.post<{ elements: DcpNetNewTermsConditions[] }>(
            this.getTermsConditionsServiceBaseUrl(),
            {
              technicalName: canonicalId.productId.id,
              editions: [
                canonicalId.entitlementRuleId
              ],
              language: currentLanguage,
              salesOrg: payload.salesOrg,
              date: moment().format('YYYY-MM-DD'),
              transactionType: payload.transactionType,
              contractData: payload.contractData,
              country: this.countrySelectorService.getCurrentCountry().isocode,
            }).pipe(
            catchError(error => {
              this.dcpGlobalMessageService.addMessage(error, this?.constructor?.name, 'errorLogging');
              return of(error);
            }),
            map(nt => nt?.elements))));

    }
    return of(undefined);
  }

  /**
   * Returns the base url of the terms&conditions service as configured in the environment.
   */
  private getTermsConditionsServiceBaseUrl(): string {
    return this.configService.getString('service.termsConditions.baseUrl') + this.configService.getString('service.termsConditions.postfix');
  }

  getTermsConditionsByEntryProduct(entry: any, salesOrg?: SalesOrg): Observable<TermsAndConditionsCanonicalIdMap | undefined> {
    if (salesOrg?.salesOrganizationERP) {
      return this.getTermsMapLoaded(entry, salesOrg)
    } else {
      const salesOrgPayload: SalesOrgPayload = {
        vendorId: entry?.vendor?.code || entry?.product?.vendor?.code,
        countryIsoCode: this.countrySelectorService.getCurrentCountry().isocode,
        httpParams: {
          default: true
        }
      };
      return this.dcpConfigService.getSalesOrg(salesOrgPayload)
        .pipe(
          switchMap((salesOrgLoaded) =>
            this.getTermsMapLoaded(entry, salesOrgLoaded))
        );
    }
  }

  /**
   * Load T&Cs by canonical id and sales org.
   *
   * @param entry
   * @param isToggleEnabled
   * @param salesOrg
   */
  getTermsMapLoaded(entry, salesOrg): Observable<TermsAndConditionsCanonicalIdMap | undefined> {
    return combineLatest([
      this.transactionTypeService.getTransactionTypeConfigurationJson(entry?.transactionType, CONFIGURATION_KEY),
    ]).pipe(
      switchMap(([transactionTypeConfig]) => {
          const canonicalId: CanonicalId = this.productUtilitiesService.getCanonicalId(entry.product);

          const payload: DcpTemplateTermsConditionsData = {};
          payload.salesOrg = salesOrg?.salesOrganizationERP;
          if (transactionTypeConfig?.shouldFilterEntitlements) {
            payload.transactionType = entry?.transactionType;

            const tcParamsJson = this.orderEntryService.getTermsAndConditionsParams(entry);
            payload.contractData = {
              contractId: tcParamsJson?.contractId,
              startDate: tcParamsJson?.startDate,
              poNumber: tcParamsJson?.poNumber,
              caseId: tcParamsJson?.caseId,
              quoteId: tcParamsJson?.quoteId,
              templateId: tcParamsJson?.templateId,
            };
          }

          this.store.dispatch(TermsAndConditionsActions.get({canonicalId, payload}));
          return this.store.select(fromTermsAndConditions.selectByCanonicalId(canonicalId));
        }
      )
    );
  }

  /**
   * Wrapping product's bundle templates into order entry objects to pass them to terms-conditions retrieving api
   *
   * @param product
   * @param firstEntryId
   */
  createTermsConditionsEntriesFromProduct(product, firstEntryId?) {
    const tcEntries: OrderEntry[] = [];
    product?.bundleTemplates?.forEach(editionBundleTemplate => {
      if (editionBundleTemplate.visible && editionBundleTemplate.transactionTypes && editionBundleTemplate.supplierAlternativeAID) {
        const tcEntry: any = {
          product: {
            ...editionBundleTemplate,
            classifications: product?.classifications,
          },
          transactionType: editionBundleTemplate.transactionTypes[0]?.code,
          vendor: product?.vendor
        };
        tcEntries.push(tcEntry);
        if (firstEntryId && editionBundleTemplate.id && firstEntryId === editionBundleTemplate.id) {
          this.swapFirstLast(tcEntries);
        }
      }
    });
    return tcEntries;
  }

  /**
   * Swapping first and last elements of the array
   *
   * @param array
   */
  swapFirstLast(array) {
    [array[0], array[array.length - 1]] = [array[array.length - 1], array[0]];
    return array;
  }
}
